import { TextField, type TextFieldProps } from '@mui/material';
import {
  Controller,
  type Control,
  type FieldPathByValue,
  type FieldValues,
  type RegisterOptions,
} from 'react-hook-form';

type FormTextFieldRules<T extends FieldValues> = Omit<
  RegisterOptions<T, FieldPathByValue<T, string | undefined>>,
  'required'
>;

type FormTextFieldProps<T extends FieldValues> = Omit<
  TextFieldProps,
  'error' | 'label' | 'name' | 'onBlur' | 'onChange' | 'required' | 'value'
> & {
  name: FieldPathByValue<T, string | undefined>;
  control: Control<T>;
  label?: string;
  required?: boolean;
  requiredMessage?: string;
  rules?: FormTextFieldRules<T>;
};

export function FormTextField<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  requiredMessage,
  rules,
  helperText,
  ...props
}: FormTextFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ ...rules, required: required ? requiredMessage || true : false }}
      render={({ field, fieldState }) => (
        <TextField
          {...props}
          {...field}
          label={label ?? field.name}
          required={required}
          error={!!fieldState.error}
          helperText={fieldState.error?.message ?? helperText}
        />
      )}
    />
  );
}
